/**
 * Mobile Menu System - Complete Solution (Animated Submenus)
 * Drop-in replacement: Enables animated open/close for mobile menu submenus.
 */

(function () {
  "use strict";

  // ============================================================================
  // Configuration
  // ============================================================================

  const CONFIG = {
    mobileBreakpoint: null, // Calculated on init
    selectors: {
      mobileToggle: ".mobile-menu-toggle",
      mobileMenu: "#mobile-menu",
      mobileMenuClose: ".mobile-menu__close",
      overlay: ".mobile-menu-overlay",
      desktopHeader: ".site-header--desktop",
      mobileHeader: ".site-header--mobile",
    },
    submenuToggle: {
      html: '<button type="button" class="submenu-toggle" aria-expanded="false"><span class="submenu-toggle__icon">▼</span></button>',
      buttonClass: "submenu-toggle",
      iconClass: "submenu-toggle__icon",
    },
  };

  // ============================================================================
  // State
  // ============================================================================

  let state = {
    isInitialized: false,
    mobileBreakpoint: null,
    cachedElements: {},
    submenuToggles: new Map(), // Track toggle buttons for cleanup
  };

  // ============================================================================
  // Utility Functions
  // ============================================================================

  function getMobileBreakpoint() {
    if (state.mobileBreakpoint !== null) return state.mobileBreakpoint;
    const breakpoint = getComputedStyle(document.documentElement)
      .getPropertyValue("--mobile-breakpoint")
      .trim();
    state.mobileBreakpoint = parseFloat(breakpoint) * 16;
    return state.mobileBreakpoint;
  }

  function isMobile() {
    return window.innerWidth <= getMobileBreakpoint();
  }

  function cacheElements() {
    state.cachedElements = {
      mobileToggle: document.querySelector(CONFIG.selectors.mobileToggle),
      mobileMenu: document.querySelector(CONFIG.selectors.mobileMenu),
      mobileMenuClose: document.querySelector(CONFIG.selectors.mobileMenuClose),
      overlay: document.querySelector(CONFIG.selectors.overlay),
      desktopHeader: document.querySelector(CONFIG.selectors.desktopHeader),
      mobileHeader: document.querySelector(CONFIG.selectors.mobileHeader),
    };

    return state.cachedElements;
  }

  function getFocusableElements(container) {
    const elements = container.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    return {
      first: elements[0],
      last: elements[elements.length - 1],
      all: Array.from(elements),
    };
  }

  // ============================================================================
  // Submenu Toggle Buttons (Mobile)
  // ============================================================================

  function addSubmenuToggle(parentItem) {
    const link = parentItem.querySelector(
      ":scope > a, :scope > .menu-item-text",
    );
    const submenu = parentItem.querySelector(":scope > .sub-menu");

    if (!link || !submenu) return;

    // Prevent duplicates
    if (parentItem.querySelector("." + CONFIG.submenuToggle.buttonClass)) {
      return;
    }

    // Create toggle button
    const buttonWrapper = document.createElement("div");
    buttonWrapper.innerHTML = CONFIG.submenuToggle.html;
    const toggleButton = buttonWrapper.firstChild;

    // Set up ARIA
    const submenuId =
      submenu.id || "submenu-" + Math.random().toString(36).substr(2, 9);
    submenu.id = submenuId;
    toggleButton.setAttribute("aria-controls", submenuId);
    toggleButton.setAttribute(
      "aria-label",
      `Toggle ${link.textContent.trim()} submenu`,
    );

    // Insert button after the link/span
    link.parentNode.insertBefore(toggleButton, link.nextSibling);

    // Store reference for cleanup
    state.submenuToggles.set(toggleButton, {
      parentItem,
      submenu,
      link,
    });

    // Add click handler
    toggleButton.addEventListener("click", handleSubmenuToggle);

    // Keep parent link clickable for navigation
    link.addEventListener("click", handleParentLinkClick);

    // Set ARIA-hidden (start closed)
    submenu.setAttribute("aria-hidden", "true");

    // Update visibility based on viewport
    updateSubmenuToggleVisibility(toggleButton);
  }

  function handleSubmenuToggle(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;
    const data = state.submenuToggles.get(button);
    if (!data) return;

    const { parentItem, submenu } = data;
    const isOpen = parentItem.classList.contains("is-open");

    // Close sibling submenus at same level
    const siblings = parentItem.parentNode.querySelectorAll(
      ":scope > .menu-item.is-open",
    );
    siblings.forEach((sibling) => {
      if (sibling !== parentItem) {
        closeSubmenu(sibling);
      }
    });

    // Toggle current submenu
    if (isOpen) {
      closeSubmenu(parentItem);
    } else {
      openSubmenu(parentItem);
    }
  }

  function handleParentLinkClick(e) {
    const link = e.currentTarget;
    const href = link.getAttribute("href");
    const isSpan = link.tagName.toLowerCase() === "span";
    if (isSpan || href === "#" || href === "") {
      e.preventDefault();
    }
  }

  // --- Updated for aria-hidden and animation compatibility ---

  function openSubmenu(parentItem) {
    parentItem.classList.add("is-open");

    const button = parentItem.querySelector(
      "." + CONFIG.submenuToggle.buttonClass,
    );
    if (button) {
      button.setAttribute("aria-expanded", "true");
      const icon = button.querySelector("." + CONFIG.submenuToggle.iconClass);
      if (icon) icon.style.transform = "rotate(180deg)";
    }
    const submenu = parentItem.querySelector(":scope > .sub-menu");
    if (submenu) submenu.setAttribute("aria-hidden", "false");
  }

  function closeSubmenu(parentItem) {
    parentItem.classList.remove("is-open");

    const button = parentItem.querySelector(
      "." + CONFIG.submenuToggle.buttonClass,
    );
    if (button) {
      button.setAttribute("aria-expanded", "false");
      const icon = button.querySelector("." + CONFIG.submenuToggle.iconClass);
      if (icon) icon.style.transform = "";
    }
    const submenu = parentItem.querySelector(":scope > .sub-menu");
    if (submenu) submenu.setAttribute("aria-hidden", "true");
  }

  function updateSubmenuToggleVisibility(button) {
    const mobile = isMobile();
    button.style.display = mobile ? "" : "none";
  }

  function initSubmenuToggles() {
    const { mobileMenu } = state.cachedElements;
    if (!mobileMenu) return;

    const parentItems = mobileMenu.querySelectorAll(".menu-item.has-children");
    parentItems.forEach(addSubmenuToggle);
  }

  // ============================================================================
  // Mobile Menu Open/Close
  // ============================================================================

  function openMenu() {
    const { mobileToggle, mobileMenu, mobileMenuClose, overlay } =
      state.cachedElements;

    mobileToggle.setAttribute("aria-expanded", "true");
    mobileMenu.classList.add("is-open");
    mobileMenu.setAttribute("aria-hidden", "false");
    if (overlay) overlay.classList.add("is-visible");

    document.body.style.overflow = "hidden";

    if (mobileMenuClose) {
      setTimeout(() => mobileMenuClose.focus(), 100);
    }
  }

  function closeMenu() {
    const { mobileToggle, mobileMenu, overlay } = state.cachedElements;

    mobileToggle.setAttribute("aria-expanded", "false");
    mobileMenu.classList.remove("is-open");
    mobileMenu.setAttribute("aria-hidden", "true");
    if (overlay) overlay.classList.remove("is-visible");

    document.body.style.overflow = "";

    mobileToggle.focus();

    // Close all submenus
    const openSubmenus = mobileMenu.querySelectorAll(".menu-item.is-open");
    openSubmenus.forEach(closeSubmenu);
  }

  function toggleMenu() {
    const { mobileMenu } = state.cachedElements;

    if (mobileMenu.classList.contains("is-open")) {
      closeMenu();
    } else {
      openMenu();
    }

    // Add bounce animation
    const { mobileToggle } = state.cachedElements;
    if (mobileToggle) {
      mobileToggle.classList.add("is-animating");
      setTimeout(() => {
        mobileToggle.classList.remove("is-animating");
      }, 300);
    }
  }

  function handleCloseButtonClick(e) {
    const button = e.currentTarget;
    button.classList.add("is-animating");
    setTimeout(() => {
      button.classList.remove("is-animating");
    }, 250);

    closeMenu();
  }

  function handleMenuFocusTrap(e) {
    const { mobileMenu } = state.cachedElements;

    if (e.key !== "Tab" || !mobileMenu.classList.contains("is-open")) return;

    const focusable = getFocusableElements(mobileMenu);

    if (e.shiftKey) {
      if (document.activeElement === focusable.first) {
        e.preventDefault();
        focusable.last.focus();
      }
    } else {
      if (document.activeElement === focusable.last) {
        e.preventDefault();
        focusable.first.focus();
      }
    }
  }

  function initMobileMenu() {
    const { mobileToggle, mobileMenuClose, mobileMenu, overlay } =
      state.cachedElements;

    if (!mobileToggle || !mobileMenu) return;

    mobileMenu.setAttribute("aria-hidden", "true");

    mobileToggle.addEventListener("click", toggleMenu);

    if (mobileMenuClose) {
      mobileMenuClose.addEventListener("click", handleCloseButtonClick);
    }

    if (overlay) {
      overlay.addEventListener("click", closeMenu);
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && mobileMenu.classList.contains("is-open")) {
        closeMenu();
      }
    });

    mobileMenu.addEventListener("keydown", handleMenuFocusTrap);

    mobileMenu.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (link && !link.closest(".menu-item.has-children > a")) {
        closeMenu();
      }
      if (link && link.closest(".sub-menu")) {
        closeMenu();
      }
    });
  }

  // ============================================================================
  // Desktop Submenu Accessibility
  // ============================================================================

  function setupDesktopSubmenuAccessibility(link, submenu, parent) {
    const submenuId =
      "desktop-submenu-" + Math.random().toString(36).substr(2, 9);
    submenu.setAttribute("id", submenuId);
    link.setAttribute("aria-haspopup", "true");
    link.setAttribute("aria-expanded", "false");
    link.setAttribute("aria-controls", submenuId);

    link.addEventListener("keydown", (e) => {
      const isExpanded = link.getAttribute("aria-expanded") === "true";

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const newState = !isExpanded;
        link.setAttribute("aria-expanded", newState);

        if (newState) {
          const firstSubLink = submenu.querySelector("a");
          if (firstSubLink) {
            setTimeout(() => firstSubLink.focus(), 50);
          }
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!isExpanded) {
          link.setAttribute("aria-expanded", "true");
        }
        const firstSubLink = submenu.querySelector("a");
        if (firstSubLink) {
          setTimeout(() => firstSubLink.focus(), 50);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (!isExpanded) {
          link.setAttribute("aria-expanded", "true");
          const firstSubLink = submenu.querySelector("a");
          if (firstSubLink) {
            setTimeout(() => firstSubLink.focus(), 50);
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        link.setAttribute("aria-expanded", "false");
        link.focus();
      }
    });

    const submenuItems = submenu.querySelectorAll("a");
    submenuItems.forEach((item, index) => {
      item.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          const nextIndex = (index + 1) % submenuItems.length;
          submenuItems[nextIndex].focus();
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          if (index === 0) {
            link.focus();
            link.setAttribute("aria-expanded", "false");
          } else {
            submenuItems[index - 1].focus();
          }
        } else if (e.key === "Escape" || e.key === "ArrowLeft") {
          e.preventDefault();
          link.setAttribute("aria-expanded", "false");
          link.focus();
        }
      });
    });

    parent.addEventListener("mouseenter", () => {
      link.setAttribute("aria-expanded", "true");
    });

    parent.addEventListener("mouseleave", () => {
      if (!parent.contains(document.activeElement)) {
        link.setAttribute("aria-expanded", "false");
      }
    });

    parent.addEventListener("focusout", (e) => {
      if (!parent.contains(e.relatedTarget)) {
        link.setAttribute("aria-expanded", "false");
      }
    });
  }

  function initDesktopSubmenus() {
    const { desktopHeader } = state.cachedElements;
    if (!desktopHeader) return;

    const desktopSubMenuToggles = desktopHeader.querySelectorAll(
      ".menu-item.has-children > a, .menu-item.has-children > .menu-item-text",
    );

    desktopSubMenuToggles.forEach((link) => {
      const parent = link.closest(".menu-item");
      const submenu = parent.querySelector(".sub-menu");

      if (submenu) {
        setupDesktopSubmenuAccessibility(link, submenu, parent);
      }
    });
  }

  // ============================================================================
  // Window Resize Handler
  // ============================================================================

  function handleResize() {
    // Update all submenu toggle button visibility
    state.submenuToggles.forEach((data, button) => {
      updateSubmenuToggleVisibility(button);
    });
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  function init() {
    if (state.isInitialized) return;
    cacheElements();
    initMobileMenu();
    initSubmenuToggles();
    initDesktopSubmenus();

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    });

    state.isInitialized = true;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
